<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Loading state -->
    <empty-state v-if="isLoading" :title="$t('loading')" :loading="true" />

    <!-- Empty state: No data -->
    <empty-state
      v-else-if="!transactions.length"
      icon="mdi-bank-transfer-out"
      :title="$t('no_data')"
      :subtitle="$t('no_transactions_for_selected_period')"
    />

    <template v-else>
      <!-- Summary Card -->
      <v-card class="mb-4 ordera-box-shadow">
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="4">
              <div class="text-center">
                <div class="text-h6 primary--text">{{ formatCurrency(summary.deposits) }}</div>
                <div class="text-caption grey--text">{{ $t('total_deposits') }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-center">
                <div class="text-h6 error--text">{{ formatCurrency(summary.withdrawals) }}</div>
                <div class="text-caption grey--text">{{ $t('total_withdrawals') }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-center">
                <div class="text-h6" :class="summary.balance >= 0 ? 'success--text' : 'error--text'">
                  {{ formatCurrency(summary.balance) }}
                </div>
                <div class="text-caption grey--text">{{ $t('current_balance') }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Desktop View -->
      <v-data-table
        v-if="!device.isMobile"
        :headers="headers"
        :items="customSearch(transactions, headers, search)"
        class="ordera-box-shadow striped-table"
        hide-default-footer
        disable-pagination
        mobile-breakpoint="0"
      >
        <template v-slot:item.date="{ item }">
          {{ formatDate(item.date) }}
        </template>
        <template v-slot:item.type="{ item }">
          <v-chip small :color="item.type === 'deposit' ? 'success' : 'error'" dark>
            {{ $t(item.type) }}
          </v-chip>
        </template>
        <template v-slot:item.amount="{ item }">
          <span :class="item.type === 'deposit' ? 'success--text' : 'error--text'" class="font-weight-bold">
            {{ item.type === 'deposit' ? '+' : '-' }}{{ formatCurrency(item.amount) }}
          </span>
        </template>
        <template v-slot:item.balance="{ item }">
          <span class="font-weight-bold" :class="item.balance >= 0 ? 'success--text' : 'error--text'">
            {{ formatCurrency(item.balance) }}
          </span>
        </template>
      </v-data-table>

      <!-- Mobile View -->
      <mobile-data-cards
        v-else
        :headers="mobileHeaders"
        :items="customSearch(transactions, headers, search)"
        item-key="id"
        :fields-per-row="null"
        :bold-fields="['date']"
        :show-row-number="false"
        class="px-3"
      >
        <template v-slot:item.date="{ item }">
          {{ formatDate(item.date) }}
        </template>
        <template v-slot:item.type="{ item }">
          <v-chip x-small :color="item.type === 'deposit' ? 'success' : 'error'" dark>
            {{ $t(item.type) }}
          </v-chip>
        </template>
        <template v-slot:item.amount="{ item }">
          <span :class="item.type === 'deposit' ? 'success--text' : 'error--text'" class="font-weight-bold">
            {{ item.type === 'deposit' ? '+' : '-' }}{{ formatCurrency(item.amount) }}
          </span>
        </template>
        <template v-slot:item.balance="{ item }">
          <span class="font-weight-bold" :class="item.balance >= 0 ? 'success--text' : 'error--text'">
            {{ formatCurrency(item.balance) }}
          </span>
        </template>
      </mobile-data-cards>
    </template>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import { mockCustomerCreditTransactions } from "@/data/customerCreditTransactions";

export default {
  name: "CustomerReportTabCreditTransactions",
  mixins: [hasSnackbar],
  components: {
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      isLoading: false,
      transactions: [],
    };
  },
  computed: {
    dates() {
      return this.$store.state.report?.filters?.dates || { from: null, to: null };
    },
    search() {
      return this.$store.state.report?.filters?.search || "";
    },
    summary() {
      const deposits = this.transactions
        .filter(t => t.type === 'deposit')
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      
      const withdrawals = this.transactions
        .filter(t => t.type === 'withdrawal')
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      
      return {
        deposits,
        withdrawals,
        balance: deposits - withdrawals,
      };
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("date"), value: "date" },
        { text: this.$t("type"), value: "type" },
        { text: this.$t("description"), value: "description" },
        { text: this.$t("amount"), value: "amount", align: "right" },
        { text: this.$t("balance"), value: "balance", align: "right" },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("date"), value: "date", cols: 6 },
        { text: this.$t("type"), value: "type", cols: 6 },
        { text: this.$t("description"), value: "description", cols: 12 },
        { text: this.$t("amount"), value: "amount", cols: 6 },
        { text: this.$t("balance"), value: "balance", cols: 6 },
      ];
    },
  },
  watch: {
    dates: {
      handler() {
        this.fetchTransactions();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchTransactions();
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      return this.localeDate(date);
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "0.00";
      return parseFloat(amount).toFixed(2);
    },
    async fetchTransactions() {
      this.isLoading = true;
      try {
        // TODO: Implement API call
        // const response = await reportService.getCustomerCreditTransactions({
        //   date_from: this.dates.from,
        //   date_to: this.dates.to
        // });
        
        // Using mock data for now
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
        this.transactions = mockCustomerCreditTransactions.map((transaction, index) => ({
          ...transaction,
          num: index + 1,
        }));
      } catch (error) {
        console.error("Error fetching transactions:", error);
        this.showErrorSnackbar(error, this.$t("error_loading_transactions"));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
